import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PERMISSIONS } from 'app/config/constants';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { hasPermissions } from 'app/shared/auth/private-route';
import { IGrid } from 'app/shared/model/grid.model';
import React, { useEffect, useState } from 'react';
import { Translate, translate } from 'react-jhipster';
import { Button, Card, Input, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import GridMap, { GridMapGrid } from './grid-map';
import GridDevicesPanel from './grid-devices-panel';
import { createEntity, deleteEntity, getEntities, reset, updateEntity } from './grid-management.reducer';
import { CELL_SIZE, computeGridCornerPoints, GRID_DIM, gridToBounds, gridToOrigin, latLngsToGridPoints, LatLng } from './utils';
import './grid-manager.scss';

const DEFAULT_CENTER: LatLng = [39.8283, -98.5795];
const LAT_LNG_PATTERN = /^-?\d*\.?\d*$/;

export const GridManagement = () => {
  const dispatch = useAppDispatch();

  const gridList: IGrid[] = useAppSelector(state => state.gridManagement.entities);
  const loading = useAppSelector(state => state.gridManagement.loading);
  const updating = useAppSelector(state => state.gridManagement.updating);
  const updateSuccess = useAppSelector(state => state.gridManagement.updateSuccess);
  const permissions = useAppSelector(state => state.authentication.account.permissions);
  const canEditGrids = hasPermissions(permissions, [PERMISSIONS.GRID_EDIT, PERMISSIONS.ALL]);

  const [placing, setPlacing] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [viewingId, setViewingId] = useState<number | null>(null);
  const [pendingOrigin, setPendingOrigin] = useState<LatLng | null>(null);
  const [latText, setLatText] = useState('');
  const [lngText, setLngText] = useState('');
  const [pendingName, setPendingName] = useState('');
  const [pendingCellSize, setPendingCellSize] = useState(CELL_SIZE);
  const [pendingDimension, setPendingDimension] = useState(GRID_DIM);
  const [deleteTarget, setDeleteTarget] = useState<IGrid | null>(null);

  useEffect(() => {
    dispatch(getEntities({}));
    return () => {
      dispatch(reset());
    };
  }, []);

  const setOrigin = (origin: LatLng | null) => {
    setPendingOrigin(origin);
    setLatText(origin ? String(origin[0]) : '');
    setLngText(origin ? String(origin[1]) : '');
  };

  useEffect(() => {
    if (updateSuccess) {
      setPlacing(false);
      setEditingId(null);
      setViewingId(null);
      setOrigin(null);
      setPendingName('');
      setPendingCellSize(CELL_SIZE);
      setPendingDimension(GRID_DIM);
      setDeleteTarget(null);
    }
  }, [updateSuccess]);

  useEffect(() => {
    if (!placing && editingId == null && viewingId == null) {
      return;
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        cancelPlacement();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [placing, editingId, viewingId]);

  const startPlacement = () => {
    setPlacing(true);
    setEditingId(null);
    setViewingId(null);
    setOrigin(null);
    setPendingName('');
    setPendingCellSize(CELL_SIZE);
    setPendingDimension(GRID_DIM);
  };

  const cancelPlacement = () => {
    setPlacing(false);
    setEditingId(null);
    setViewingId(null);
    setOrigin(null);
    setPendingName('');
    setPendingCellSize(CELL_SIZE);
    setPendingDimension(GRID_DIM);
  };

  const handleOriginPick = (lat: number, lng: number) => {
    setOrigin([lat, lng]);
  };

  const isPendingConfigValid = pendingCellSize > 0 && Number.isInteger(pendingDimension) && pendingDimension > 0;

  const saveGrid = () => {
    if (!pendingOrigin || !pendingName.trim() || !isPendingConfigValid) {
      return;
    }
    const payload = {
      name: pendingName.trim(),
      cellSize: pendingCellSize,
      gridDimension: pendingDimension,
      ...latLngsToGridPoints(computeGridCornerPoints(pendingOrigin[0], pendingOrigin[1], pendingCellSize, pendingDimension)),
    };
    if (editingId != null) {
      dispatch(updateEntity({ id: editingId, ...payload }));
    } else {
      dispatch(createEntity(payload));
    }
  };

  const toggleEdit = (grid: IGrid) => {
    if (editingId === grid.id) {
      cancelPlacement();
      return;
    }
    setPlacing(false);
    setViewingId(null);
    setEditingId(grid.id ?? null);
    setOrigin(gridToOrigin(grid));
    setPendingName(grid.name ?? '');
    setPendingCellSize(grid.cellSize ?? CELL_SIZE);
    setPendingDimension(grid.gridDimension ?? GRID_DIM);
  };

  const viewGrid = (grid: IGrid) => {
    if (viewingId === grid.id) {
      setViewingId(null);
      return;
    }
    setPlacing(false);
    setEditingId(null);
    setOrigin(null);
    setViewingId(grid.id ?? null);
  };

  const confirmDelete = () => {
    if (deleteTarget?.id != null) {
      dispatch(deleteEntity(deleteTarget.id));
    }
  };

  const grids: GridMapGrid[] = gridList
    .map(grid => {
      const bounds = gridToBounds(grid);
      return bounds && grid.id != null && grid.id !== editingId ? { id: grid.id, bounds } : null;
    })
    .filter((grid): grid is GridMapGrid => grid != null);

  const viewingGrid = viewingId != null ? gridList.find(grid => grid.id === viewingId) ?? null : null;
  const viewingOrigin = viewingGrid ? gridToOrigin(viewingGrid) : null;

  const mapCenter = pendingOrigin ?? viewingOrigin ?? grids[0]?.bounds?.[0] ?? DEFAULT_CENTER;

  const handleOriginTextChange = (raw: string, setText: (value: string) => void, updateOrigin: (value: number) => void) => {
    if (!LAT_LNG_PATTERN.test(raw)) {
      return;
    }
    setText(raw);
    const parsed = Number(raw);
    if (raw !== '' && !Number.isNaN(parsed)) {
      updateOrigin(parsed);
    }
  };

  const updateOriginLat = (lat: number) => {
    if (Number.isNaN(lat)) {
      return;
    }
    setPendingOrigin([lat, pendingOrigin ? pendingOrigin[1] : 0]);
  };
  const updateOriginLng = (lng: number) => {
    if (Number.isNaN(lng)) {
      return;
    }
    setPendingOrigin([pendingOrigin ? pendingOrigin[0] : 0, lng]);
  };

  const gridForm = (
    <div className="grid-form-panel">
      <div className="grid-form-fields">
        <div className="grid-form-title">
          {editingId != null ? (
            <Translate contentKey="rainspotApp.gridManagement.editTitle">Edit Grid</Translate>
          ) : (
            <Translate contentKey="rainspotApp.gridManagement.addTitle">Add Grid</Translate>
          )}
        </div>
        <div className="grid-form-field">
          <label htmlFor="grid-name">
            <Translate contentKey="rainspotApp.gridManagement.name">Name</Translate>
          </label>
          <Input
            id="grid-name"
            autoFocus
            bsSize="sm"
            data-cy="gridNameInput"
            value={pendingName}
            onChange={e => setPendingName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                saveGrid();
              }
            }}
          />
        </div>
        <div className="grid-form-row">
          <div className="grid-form-field">
            <label htmlFor="grid-lat">
              <Translate contentKey="rainspotApp.gridManagement.latitude">Latitude</Translate>
            </label>
            <Input
              id="grid-lat"
              type="text"
              inputMode="decimal"
              bsSize="sm"
              data-cy="gridLatInput"
              value={latText}
              onChange={e => handleOriginTextChange(e.target.value, setLatText, updateOriginLat)}
            />
          </div>
          <div className="grid-form-field">
            <label htmlFor="grid-lng">
              <Translate contentKey="rainspotApp.gridManagement.longitude">Longitude</Translate>
            </label>
            <Input
              id="grid-lng"
              type="text"
              inputMode="decimal"
              bsSize="sm"
              data-cy="gridLngInput"
              value={lngText}
              onChange={e => handleOriginTextChange(e.target.value, setLngText, updateOriginLng)}
            />
          </div>
        </div>
        <div className="grid-form-row">
          <div className="grid-form-field">
            <label htmlFor="grid-dimension">
              <Translate contentKey="rainspotApp.gridManagement.dimension">Grid Dimension</Translate>
            </label>
            <Input
              id="grid-dimension"
              type="number"
              step="1"
              min="1"
              bsSize="sm"
              data-cy="gridDimensionInput"
              value={pendingDimension}
              onChange={e => setPendingDimension(Number(e.target.value))}
            />
          </div>
          <div className="grid-form-field">
            <label htmlFor="grid-cell-size">
              <Translate contentKey="rainspotApp.gridManagement.cellSize">Cell Size (degrees)</Translate>
            </label>
            <Input
              id="grid-cell-size"
              type="number"
              step="0.01"
              min="0.01"
              bsSize="sm"
              data-cy="gridCellSizeInput"
              value={pendingCellSize}
              onChange={e => setPendingCellSize(Number(e.target.value))}
            />
          </div>
        </div>
      </div>
      {editingId != null && <GridDevicesPanel gridId={editingId} />}
      <div className="grid-form-footer">
        <Button color="info" size="sm" onClick={cancelPlacement}>
          <Translate contentKey="entity.action.cancel">Cancel</Translate>
        </Button>
        <Button color="primary" size="sm" disabled={!pendingName.trim() || !isPendingConfigValid || updating} onClick={saveGrid}>
          <Translate contentKey="entity.action.save">Save</Translate>
        </Button>
      </div>
    </div>
  );

  const gridViewPanel = viewingGrid && (
    <div className="grid-form-panel">
      <div className="grid-form-fields">
        <div className="grid-form-title">
          <Translate contentKey="rainspotApp.gridManagement.viewTitle">View Grid</Translate>
        </div>
        <div className="grid-form-field">
          <label htmlFor="grid-view-name">
            <Translate contentKey="rainspotApp.gridManagement.name">Name</Translate>
          </label>
          <Input id="grid-view-name" bsSize="sm" value={viewingGrid.name ?? ''} disabled readOnly />
        </div>
        <div className="grid-form-row">
          <div className="grid-form-field">
            <label htmlFor="grid-view-lat">
              <Translate contentKey="rainspotApp.gridManagement.latitude">Latitude</Translate>
            </label>
            <Input id="grid-view-lat" bsSize="sm" value={viewingOrigin ? viewingOrigin[0].toFixed(4) : ''} disabled readOnly />
          </div>
          <div className="grid-form-field">
            <label htmlFor="grid-view-lng">
              <Translate contentKey="rainspotApp.gridManagement.longitude">Longitude</Translate>
            </label>
            <Input id="grid-view-lng" bsSize="sm" value={viewingOrigin ? viewingOrigin[1].toFixed(4) : ''} disabled readOnly />
          </div>
        </div>
        <div className="grid-form-row">
          <div className="grid-form-field">
            <label htmlFor="grid-view-dimension">
              <Translate contentKey="rainspotApp.gridManagement.dimension">Grid Dimension</Translate>
            </label>
            <Input id="grid-view-dimension" bsSize="sm" value={viewingGrid.gridDimension ?? ''} disabled readOnly />
          </div>
          <div className="grid-form-field">
            <label htmlFor="grid-view-cell-size">
              <Translate contentKey="rainspotApp.gridManagement.cellSize">Cell Size (degrees)</Translate>
            </label>
            <Input id="grid-view-cell-size" bsSize="sm" value={viewingGrid.cellSize ?? ''} disabled readOnly />
          </div>
        </div>
      </div>
      {viewingGrid.id != null && <GridDevicesPanel gridId={viewingGrid.id} />}
      <div className="grid-form-footer">
        <Button color="info" size="sm" onClick={cancelPlacement}>
          <Translate contentKey="entity.action.back">Back</Translate>
        </Button>
        {canEditGrids && (
          <Button color="primary" size="sm" data-cy="gridViewEditButton" onClick={() => toggleEdit(viewingGrid)}>
            <FontAwesomeIcon icon="pencil-alt" />
            &nbsp;
            <Translate contentKey="entity.action.edit">Edit</Translate>
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <Card className="jh-card grid-manager-card">
      <div className="grid-manager-header">
        <h2 id="grid-management-heading" data-cy="GridManagementHeading" className="d-flex align-items-center gap-2 mb-0">
          <Translate contentKey="rainspotApp.gridManagement.home.title">Grid Management</Translate>
          <span className="grid-count-badge">{gridList.length}</span>
        </h2>
        {canEditGrids && !placing && editingId == null && viewingId == null && (
          <Button color="primary" className="jh-create-entity" id="jh-create-entity" data-cy="entityCreateButton" onClick={startPlacement}>
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="rainspotApp.gridManagement.home.createLabel">Add Grid</Translate>
          </Button>
        )}
      </div>

      <div className="grid-manager-layout">
        <div className="grid-manager-map">
          {placing && !pendingOrigin && (
            <div className="grid-placement-banner">
              <FontAwesomeIcon icon="draw-polygon" />
              <span>
                <Translate contentKey="rainspotApp.gridManagement.placeInstruction">Click on the map to place a new grid</Translate>
              </span>
              <kbd>Esc</kbd>
              <Button color="link" size="sm" onClick={cancelPlacement}>
                <Translate contentKey="entity.action.cancel">Cancel</Translate>
              </Button>
            </div>
          )}
          {editingId != null && (
            <div className="grid-placement-banner">
              <FontAwesomeIcon icon="pencil-alt" />
              <span>
                <Translate contentKey="rainspotApp.gridManagement.editInstruction">Drag the pin or click on the map to move the grid</Translate>
              </span>
              <kbd>Esc</kbd>
              <Button color="link" size="sm" onClick={cancelPlacement}>
                <Translate contentKey="entity.action.cancel">Cancel</Translate>
              </Button>
            </div>
          )}
          <GridMap
            center={mapCenter}
            interactive={placing || editingId != null}
            onOriginPick={handleOriginPick}
            grids={grids}
            highlightedGridId={viewingId}
            previewOrigin={pendingOrigin}
            previewCellSize={pendingCellSize}
            previewDimension={pendingDimension}
          />
        </div>

        <div className="grid-manager-sidebar">
          {pendingOrigin && (placing || editingId != null) ? (
            gridForm
          ) : viewingGrid ? (
            gridViewPanel
          ) : (
            <>
              {!gridList.length && !loading && (
                <div className="grid-empty-state text-muted">
                  <Translate contentKey="rainspotApp.gridManagement.home.notFound">No grids found</Translate>
                </div>
              )}
              {gridList.map(grid => {
                const origin = gridToOrigin(grid);
                const sizeLabel =
                  grid.gridDimension != null
                    ? `${grid.gridDimension}x${grid.gridDimension}`
                    : (() => {
                        const bounds = gridToBounds(grid);
                        const extent = bounds ? bounds[1][0] - bounds[0][0] : null;
                        return extent != null ? `${extent.toFixed(2)}°` : null;
                      })();
                return (
                  <div
                    key={grid.id}
                    className={`grid-card ${grid.id === viewingId ? 'grid-card-active' : ''}`}
                    onClick={() => viewGrid(grid)}
                  >
                    <div className="grid-card-info">
                      <div className="grid-card-name">{grid.name}</div>
                      <div className="grid-card-meta">
                        <span>{origin ? `${origin[0].toFixed(4)}, ${origin[1].toFixed(4)}` : '--'}</span>
                        {sizeLabel && <span className="grid-size-badge">{sizeLabel}</span>}
                      </div>
                    </div>
                    {canEditGrids && (
                      <div className="grid-card-actions">
                        <button
                          type="button"
                          className="grid-icon-btn"
                          data-cy="gridEditButton"
                          title={translate('entity.action.edit')}
                          onClick={e => {
                            e.stopPropagation();
                            toggleEdit(grid);
                          }}
                        >
                          <FontAwesomeIcon icon="pencil-alt" />
                        </button>
                        <button
                          type="button"
                          className="grid-icon-btn grid-icon-btn-danger"
                          data-cy="gridDeleteButton"
                          title={translate('entity.action.delete')}
                          onClick={e => {
                            e.stopPropagation();
                            setDeleteTarget(grid);
                          }}
                        >
                          <FontAwesomeIcon icon="trash" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>

      <Modal isOpen={!!deleteTarget} toggle={() => setDeleteTarget(null)}>
        <ModalHeader toggle={() => setDeleteTarget(null)} data-cy="gridManagementDeleteDialogHeading">
          <Translate contentKey="entity.delete.title">Confirm delete operation</Translate>
        </ModalHeader>
        <ModalBody id="rainspotApp.gridManagement.delete.question">
          <Translate contentKey="rainspotApp.gridManagement.delete.question" interpolate={{ name: deleteTarget?.name }}>
            Are you sure you want to delete this grid?
          </Translate>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setDeleteTarget(null)}>
            <FontAwesomeIcon icon="ban" />
            &nbsp;
            <Translate contentKey="entity.action.cancel">Cancel</Translate>
          </Button>
          <Button
            id="jhi-confirm-delete-grid-management"
            data-cy="entityConfirmDeleteButton"
            color="danger"
            disabled={updating}
            onClick={confirmDelete}
          >
            <FontAwesomeIcon icon="trash" />
            &nbsp;
            <Translate contentKey="entity.action.delete">Delete</Translate>
          </Button>
        </ModalFooter>
      </Modal>
    </Card>
  );
};

export default GridManagement;
